import React, { useState } from 'react';
import AdminBlogList from './AdminBlogList';
import AdminBlogEditor from './AdminBlogEditor';
import AdminLanguages from './AdminLanguages';
import AdminStats from './AdminStats';

type AdminView = 'blogs' | 'editor' | 'languages' | 'stats';

const S = {
  page: {
    minHeight: '100vh',
    background: '#f9fafb',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  } as React.CSSProperties,
  loginWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #07091A 0%, #0D1020 100%)',
  } as React.CSSProperties,
  loginCard: {
    background: '#fff',
    borderRadius: '20px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '380px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  } as React.CSSProperties,
  loginTitle: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: '#07091A',
    marginBottom: '0.25rem',
  } as React.CSSProperties,
  loginSub: {
    color: '#6b6b8a',
    fontSize: '0.9rem',
    marginBottom: '1.75rem',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '10px',
    border: '1.5px solid #e5e7eb',
    fontSize: '1rem',
    outline: 'none',
    marginBottom: '1rem',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  } as React.CSSProperties,
  loginBtn: {
    width: '100%',
    padding: '0.8rem',
    background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  errMsg: {
    color: '#ef4444',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  header: {
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    padding: '0 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '60px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  } as React.CSSProperties,
  headerLogo: {
    fontWeight: 800,
    fontSize: '1.1rem',
    color: '#07091A',
  } as React.CSSProperties,
  tabs: {
    display: 'flex',
    gap: '0.25rem',
  } as React.CSSProperties,
  tab: {
    padding: '0.4rem 1rem',
    border: 'none',
    background: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#6b6b8a',
    fontFamily: 'inherit',
    fontWeight: 500,
  } as React.CSSProperties,
  tabActive: {
    background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
    color: '#fff',
    fontWeight: 700,
  } as React.CSSProperties,
  logoutBtn: {
    padding: '0.35rem 0.9rem',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#6b6b8a',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  body: {
    padding: '1.5rem',
  } as React.CSSProperties,
};

const AdminPanel: React.FC = () => {
  const [secret, setSecret] = useState(() => sessionStorage.getItem('cookai-admin-secret') ?? '');
  const [secretInput, setSecretInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [view, setView] = useState<AdminView>('blogs');
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);

  const isAuthed = !!secret;

  const handleLogin = () => {
    if (!secretInput.trim()) {
      setAuthError('Please enter the admin secret.');
      return;
    }
    sessionStorage.setItem('cookai-admin-secret', secretInput.trim());
    setSecret(secretInput.trim());
    setAuthError('');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('cookai-admin-secret');
    setSecret('');
    setSecretInput('');
  };

  const handleNewBlog = () => {
    setEditingBlogId(null);
    setView('editor');
  };

  const handleEditBlog = (id: string) => {
    setEditingBlogId(id);
    setView('editor');
  };

  const handleEditorDone = () => {
    setView('blogs');
    setEditingBlogId(null);
  };

  if (!isAuthed) {
    return (
      <div style={S.loginWrap}>
        <div style={S.loginCard}>
          <p style={S.loginTitle}>Cook AI Admin</p>
          <p style={S.loginSub}>Enter your admin secret to continue</p>
          <input
            type="password"
            placeholder="Admin secret..."
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            style={S.input}
            autoFocus
          />
          {authError && <p style={S.errMsg}>{authError}</p>}
          <button style={S.loginBtn} onClick={handleLogin}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const tabs: { key: AdminView; label: string }[] = [
    { key: 'blogs', label: 'Blogs' },
    { key: 'languages', label: 'Languages' },
    { key: 'stats', label: 'Stats' },
  ];

  return (
    <div style={S.page}>
      <header style={S.header}>
        <span style={S.headerLogo}>Cook AI Admin</span>
        <div style={S.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              style={{ ...S.tab, ...(view === tab.key && view !== 'editor' ? S.tabActive : {}) }}
              onClick={() => {
                setView(tab.key);
                setEditingBlogId(null);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button style={S.logoutBtn} onClick={handleLogout}>
          Sign out
        </button>
      </header>

      <div style={S.body}>
        {view === 'blogs' && (
          <AdminBlogList
            secret={secret}
            onNew={handleNewBlog}
            onEdit={handleEditBlog}
          />
        )}
        {view === 'editor' && (
          <AdminBlogEditor
            secret={secret}
            blogId={editingBlogId}
            onDone={handleEditorDone}
          />
        )}
        {view === 'languages' && <AdminLanguages secret={secret} />}
        {view === 'stats' && <AdminStats secret={secret} />}
      </div>
    </div>
  );
};

export default AdminPanel;
